import React from 'react';
import HeroSection from '../components/HeroSection';
import CategoryPills from '../components/CategoryPills';
import BrandCard from '../components/BrandCard';
import ScrollReveal from '../components/ScrollReveal';
import PriceTicker from '../components/PriceTicker';
import ReferralBanner from '../components/ReferralBanner';
import { BRANDS, CATEGORIES, TESTIMONIALS, FAQ, CRYPTO_METHODS } from '../mock';
import { Link } from 'react-router-dom';
import { ArrowRight, Wallet, Search, Gift, Star, Zap, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Home = () => {
  const featured = BRANDS.filter(b => b.featured).slice(0, 12);
  const trending = BRANDS.slice(0, 18);

  return (
    <div>
      <HeroSection />
      <PriceTicker />
      <ReferralBanner />

      {/* Categories */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-2xl md:text-3xl font-bold">Browse by category</h2>
            <Link to="/browse" className="text-sm text-[#8247E5] hover:underline flex items-center gap-1">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <CategoryPills categories={CATEGORIES} active="all" />
        </section>
      </ScrollReveal>

      {/* Featured */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold">Featured brands</h2>
              <p className="text-sm text-neutral-600 mt-1">The brands you already love, now paid with crypto.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {featured.map(b => <BrandCard key={b.id} brand={b} />)}
          </div>
        </section>
      </ScrollReveal>

      {/* How it works */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1 rounded-full bg-violet-100 text-[#8247E5] text-xs font-semibold mb-3">HOW IT WORKS</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold">Get started in <span className="gradient-text">3 easy steps</span></h2>
            <p className="text-neutral-600 mt-4 max-w-2xl mx-auto leading-relaxed">No accounts, no forms. Just pick what you want, pay in crypto, and get your codes instantly.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 relative">
            <div className="hidden md:block absolute top-1/2 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-violet-300 to-transparent" />
            {[
              { icon: Search, title: 'Pick what you want',   desc: 'Browse hundreds of top brands or refill any phone in 180+ countries.' },
              { icon: Wallet, title: 'Pay with your crypto', desc: 'Choose your coin, scan the QR, and pay from your wallet in seconds.' },
              { icon: Gift,   title: 'Get your code',        desc: 'Gift codes arrive in your inbox instantly. Ready to spend, anywhere.' },
            ].map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-neutral-200 p-8 hover:border-[#8247E5] transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-violet-500/10">
                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full bg-[#8247E5] text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #8247E515, #A855F715)' }}>
                  <s.icon className="w-5 h-5 text-[#8247E5]" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </ScrollReveal>

      {/* Swap promo */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="relative rounded-3xl overflow-hidden border border-violet-200 bg-white p-8 md:p-12">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-violet-300/40 blur-3xl animate-blob" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-fuchsia-300/40 blur-3xl animate-blob" style={{ animationDelay: '3s' }} />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-glow" />
                  NEW · Swap 20+ tokens
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900">Swap crypto <span className="gradient-text">without KYC</span></h2>
                <p className="mt-4 text-neutral-600 max-w-lg leading-relaxed">Trade Bitcoin, Ethereum, USDT and 15+ other coins in minutes. No account, no ID, no waiting. Live rates straight from the market.</p>
                <div className="mt-6 flex flex-wrap gap-4 text-sm text-neutral-700">
                  <div className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#8247E5]" /> ~5-15 min settlement</div>
                  <div className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#8247E5]" /> No sign-up</div>
                </div>
                <Link to="/swap">
                  <Button className="mt-6 h-12 px-6 shine-btn text-white text-base font-semibold rounded-xl">
                    Launch swap <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl shadow-violet-500/10 p-5 space-y-3">
                  <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">You send</div>
                  <div className="flex items-center justify-between">
                    <span className="font-display text-3xl font-bold">0.1</span>
                    <span className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5">
                      <img src="https://assets.coingecko.com/coins/images/1/large/bitcoin.png" alt="BTC" className="w-6 h-6 rounded-full" />
                      <span className="font-semibold text-sm">BTC</span>
                    </span>
                  </div>
                  <div className="flex justify-center py-1"><div className="w-8 h-8 rounded-lg border-2 border-neutral-200 bg-white flex items-center justify-center"><Layers className="w-3.5 h-3.5 text-[#8247E5]" /></div></div>
                  <div className="text-xs font-semibold text-[#8247E5] uppercase tracking-wider">You get</div>
                  <div className="flex items-center justify-between">
                    <span className="font-display text-3xl font-bold gradient-text">1.914</span>
                    <span className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
                      <img src="https://assets.coingecko.com/coins/images/279/large/ethereum.png" alt="ETH" className="w-6 h-6 rounded-full" />
                      <span className="font-semibold text-sm">ETH</span>
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 pt-2 border-t">1 BTC ≈ 19.14 ETH · Rate refreshes every 15s</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Trending */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display text-2xl md:text-3xl font-bold">Trending right now</h2>
            <Link to="/browse" className="text-sm text-[#8247E5] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {trending.map(b => <BrandCard key={b.id} brand={b} />)}
          </div>
        </section>
      </ScrollReveal>

      {/* Crypto banner */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="relative rounded-3xl overflow-hidden p-8 md:p-14" style={{ background: 'radial-gradient(circle at 30% 20%, #2D1259 0%, #170533 100%)' }}>
            <div className="glow-ring animate-blob" style={{ width: 300, height: 300, background: '#A855F7', top: '10%', left: '20%' }} />
            <div className="glow-ring animate-blob" style={{ width: 280, height: 280, background: '#EC4899', bottom: '10%', right: '10%', animationDelay: '2s' }} />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold mb-3">PAY WITH ANY CRYPTO</div>
                <h2 className="font-display text-3xl md:text-5xl font-bold text-white">Pay with any <span className="gradient-text">crypto you hold</span></h2>
                <p className="mt-4 text-white/70 max-w-md leading-relaxed">Bitcoin, Ethereum, USDT, USDC, Solana. If you have crypto, we take it. No hidden fees, no conversion charges, no surprises.</p>
                <div className="mt-6 flex items-center gap-2 text-sm text-white/60">
                  <Layers className="w-4 h-4" /> Scan-to-pay on every checkout
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {CRYPTO_METHODS.map(c => (
                  <div
                    key={c.id}
                    data-testid={`crypto-method-${c.id}`}
                    className="group relative overflow-hidden rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: `linear-gradient(135deg, ${c.color}22 0%, ${c.color}05 100%)`,
                      border: `1px solid ${c.color}40`,
                      boxShadow: `0 4px 20px -8px ${c.color}30, inset 0 0 0 1px ${c.color}18`,
                    }}
                  >
                    {/* Soft radial glow behind the icon — same treatment as brand tiles */}
                    <div
                      className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-3xl opacity-40 pointer-events-none group-hover:opacity-70 transition-opacity"
                      style={{ background: c.color }}
                    />
                    {/* Coin logo: SimpleIcons SVG in coin color if slug known, else CoinMarketCap PNG.
                        Falls back through: simpleicons → CMC → hidden. */}
                    <img
                      src={c.slug
                        ? `https://cdn.simpleicons.org/${c.slug}/${c.color.replace('#', '')}`
                        : `https://s2.coinmarketcap.com/static/img/coins/128x128/${c.cmcId}.png`}
                      alt={`${c.name} logo`}
                      className="relative z-10 h-10 w-10 object-contain drop-shadow-lg"
                      loading="lazy"
                      decoding="async"
                      data-fallback="0"
                      onError={(e) => {
                        const el = e.currentTarget;
                        const s = parseInt(el.dataset.fallback || '0');
                        if (s === 0) {
                          el.dataset.fallback = '1';
                          el.src = `https://s2.coinmarketcap.com/static/img/coins/128x128/${c.cmcId}.png`;
                        } else {
                          el.onerror = null;
                          el.style.display = 'none';
                        }
                      }}
                    />
                    <div className="relative z-10 mt-3 text-sm font-semibold text-white">{c.name}</div>
                    <div className="relative z-10 text-[10px] font-mono tracking-widest mt-0.5 uppercase" style={{ color: c.color }}>{c.ticker}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Testimonials */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl md:text-4xl font-bold">What our users say</h2>
            <p className="text-neutral-600 mt-3 max-w-xl mx-auto">Real stories from people using TokenGate every day.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-neutral-200 p-7 hover:border-[#8247E5]/40 hover:shadow-xl hover:shadow-violet-500/10 transition-all">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-[#8247E5] text-[#8247E5]" />)}
                </div>
                <p className="text-neutral-800 leading-relaxed">“{t.text}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.avatar} className="w-10 h-10 rounded-full object-cover" alt="" />
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-neutral-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </ScrollReveal>

      {/* FAQ */}
      <ScrollReveal>
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-8 text-center">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-neutral-200 px-5 data-[state=open]:border-[#8247E5]/40 transition-colors">
                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </ScrollReveal>

      {/* Final CTA */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
            style={{ background: 'radial-gradient(circle at 50% 50%, #2D1259 0%, #0A0417 100%)' }}>
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#8247E5]/40 rounded-full blur-3xl animate-blob" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-[#EC4899]/20 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3s' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/90 text-xs font-medium mb-6">
                <Zap className="w-3 h-3 text-[#C084FC]" /> Non-custodial · No KYC · 180+ countries
              </div>
              <h2 className="font-display text-3xl md:text-6xl font-bold text-white leading-tight">Ready to spend <span className="gradient-text">your crypto?</span></h2>
              <p className="mt-5 text-neutral-300 max-w-xl mx-auto leading-relaxed">Sign up in seconds or checkout as a guest. Your first gift card is just minutes away.</p>
              <Link to="/browse">
                <Button className="mt-8 h-13 px-8 shine-btn text-white text-base font-semibold rounded-xl shadow-lg shadow-violet-500/40">
                  Start shopping <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </div>
  );
};

export default Home;
